'use client';

// =============================================================================
// ContinueLearning — resume-from-lesson link
// =============================================================================
//
// A standalone "Continue from where you left off" link. Designed to sit
// prominently above the chapter tree or in the course hero, separate
// from the main enroll/continue CTA that EnrollButton renders in the
// PriceCard.
//
// Why duplicate the continue affordance? Two reasons:
//   1. The PriceCard sidebar may be scrolled off-screen on long pages;
//      a sticky/hero-level continue link is much more discoverable.
//   2. Some layouts hide the PriceCard for enrolled users entirely
//      (visual cleanup); the continue link replaces it in flow.
//
// Visual states:
//   - SSR / initial         → InlineSkeleton
//   - Anonymous             → render nothing
//   - LearningSource absent → render nothing (host hasn't wired enrollments)
//   - Not enrolled          → render nothing
//   - Enrolled, in progress → "Continue learning →" link
//   - Completed             → render nothing (course done, no resume)

import { useCurrentUser, useLearningSource } from '@akademi/site-builder-runtime';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { IslandKeyframes, InlineSkeleton } from './_shared';

export interface ContinueLearningProps {
  /** Course slug for the enrollment lookup. */
  courseSlug: string;
  /** Internal course id used in the play URL. The /learn/[courseId]/play/[lessonId]
   *  route uses the course id, not the slug. */
  courseId: string;
  /** Optional fallback lesson id — used when the user is enrolled but
   *  has no lastLessonId yet (cold-start case after enroll). Typically
   *  the first lesson in chapter 0. */
  fallbackLessonId?: string;
  /** href template; defaults to the player URL
   *  {@code /lxp/course/player/deliver/{courseId}?section={lessonId}}.
   *  Supports {@code {courseId}} and {@code {lessonId}} placeholders. */
  playHrefTemplate?: string;
  /** Label. Defaults to the catalogue wording. */
  label?: string;
}

export function ContinueLearning({
  courseSlug,
  courseId,
  fallbackLessonId,
  playHrefTemplate = '/lxp/course/player/deliver/{courseId}?section={lessonId}',
  label,
}: ContinueLearningProps) {
  const { t } = useTranslation('site-builder-blocks');
  const user = useCurrentUser<{ name?: string }>();
  const learning = useLearningSource();

  const state = useAsyncResource(async () => {
    if (!user || !learning?.enrollments) return null;
    const list = await learning.enrollments.list();
    const found = list.find((e) => e.courseSlug === courseSlug);
    if (!found) {
      return { enrolled: false as const, completed: false, lessonId: null };
    }
    return {
      enrolled: true as const,
      completed: Boolean(found.completedAt),
      lessonId: found.lastLessonId,
    };
  }, [user, learning, courseSlug]);

  if (!user) return null;
  if (!learning?.enrollments) return null;

  if (state.kind === 'loading') {
    return (
      <>
        <IslandKeyframes />
        <InlineSkeleton width="220px" />
      </>
    );
  }
  if (state.kind === 'error' || state.kind === 'empty') return null;
  if (!state.data.enrolled) return null;
  if (state.data.completed) return null;

  // Pick the lesson to jump to. Real lastLessonId from the enrollment
  // wins; if absent (e.g. user just enrolled, hasn't opened a lesson),
  // fall back to the caller-supplied first lesson.
  const lessonId = state.data.lessonId ?? fallbackLessonId;
  if (!lessonId) return null;

  const href = playHrefTemplate
    .replace('{courseId}', encodeURIComponent(courseId))
    .replace('{lessonId}', encodeURIComponent(lessonId));

  return (
    <a href={href} style={linkStyle}>
      <span style={iconStyle}>▶</span>
      <span>{label ?? t('islands.continueLearning.label')}</span>
      <span style={chevronStyle}>→</span>
    </a>
  );
}

const linkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: 'var(--space-2-5, 10px) var(--space-4, 16px)',
  borderRadius: 'var(--radius-md, 8px)',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 600,
  background: 'var(--color-brand-500, #3b5bdb)',
  color: 'var(--color-text-on-brand, #fff)',
  textDecoration: 'none',
  border: '1px solid transparent',
  transition: 'background 120ms ease-out',
};

const iconStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs, 12px)',
};

const chevronStyle: CSSProperties = {
  fontSize: 'var(--font-size-md, 16px)',
  lineHeight: 1,
};
